import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi, categoriesApi, productsApi } from '../services/api';
import {
  FaDownload, FaSort, FaSortUp, FaSortDown, FaFilter,
  FaBoxOpen, FaExclamationTriangle, FaSearchPlus, FaArrowUp, FaArrowDown,
  FaExchangeAlt, FaMoneyBillWave, FaRedo, FaTimes, FaChartBar,
  FaPrint, FaListUl
} from 'react-icons/fa';
import './StockReport.css';

const StockReport = () => {
  const [activeTab, setActiveTab] = useState('quantity');

  // Shared data
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Quantity report state
  const [qtyData, setQtyData] = useState([]);
  const [qtyFilters, setQtyFilters] = useState({ categoryId: '', search: '' });
  const [qtySort, setQtySort] = useState({ field: 'name', order: 'ASC' });

  // Detail report state
  const [detailData, setDetailData] = useState({ summary: null, transactions: [], grouped: null });
  const [detailFilters, setDetailFilters] = useState({
    productId: '', categoryId: '', type: 'all', startDate: '', endDate: '', groupBy: ''
  });

  // Fetch categories and products for filters
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          categoriesApi.getAll(),
          productsApi.getAll()
        ]);
        setCategories(catRes.data?.data?.categories || catRes.data || []);
        setProducts(prodRes.data?.data?.products || prodRes.data || []);
      } catch (err) {
        console.error('Error fetching filter data:', err);
      }
    };
    fetchFilterData();
  }, []);

  // Fetch detail report
  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (detailFilters.productId) params.productId = detailFilters.productId;
      if (detailFilters.categoryId) params.categoryId = detailFilters.categoryId;
      if (detailFilters.type !== 'all') params.type = detailFilters.type;
      if (detailFilters.startDate) params.startDate = detailFilters.startDate;
      if (detailFilters.endDate) params.endDate = detailFilters.endDate;
      if (detailFilters.groupBy) params.groupBy = detailFilters.groupBy;

      const res = await reportsApi.getStockDetail(params);
      setDetailData(res.data?.data || { summary: null, transactions: [], grouped: null });
    } catch (err) {
      console.error('Error fetching detail report:', err);
      setError('Failed to load detail report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [detailFilters]);

  // Fetch quantity report
  const fetchQuantity = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[Quantity Report] Fetching products...');
      const res = await productsApi.getAll();
      console.log('[Quantity Report] Products API Response:', res.data);

      // Handle different response structures
      let productList;
      if (Array.isArray(res.data)) {
        productList = res.data;
      } else if (Array.isArray(res.data?.data?.products)) {
        productList = res.data.data.products;
      } else if (Array.isArray(res.data?.data)) {
        productList = res.data.data;
      } else {
        console.error('[Quantity Report] Unexpected response structure:', res.data);
        productList = [];
      }
      console.log('[Quantity Report] Total products:', productList.length);

      // Filter by category
      if (qtyFilters.categoryId) {
        productList = productList.filter(p => p.CategoryId === parseInt(qtyFilters.categoryId));
        console.log('[Quantity Report] After category filter:', productList.length);
      }

      // Apply search filter
      if (qtyFilters.search) {
        const searchLower = qtyFilters.search.toLowerCase();
        productList = productList.filter(p =>
          p.name.toLowerCase().includes(searchLower) ||
          (p.barcode && p.barcode.toLowerCase().includes(searchLower))
        );
        console.log('[Quantity Report] After search filter:', productList.length);
      }

      // Map to simple format with category name
      productList = productList.map(p => ({
        id: p.id,
        name: p.name,
        barcode: p.barcode,
        category: p.Category?.name || 'Uncategorized',
        stock: p.stock
      }));

      // Sort
      const order = qtySort.order === 'DESC' ? -1 : 1;
      productList.sort((a, b) => {
        const aVal = a[qtySort.field];
        const bVal = b[qtySort.field];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === 'string') return aVal.localeCompare(bVal) * order;
        return (aVal - bVal) * order;
      });

      setQtyData(productList);
    } catch (err) {
      console.error('[Quantity Report] Error fetching products:', err);
      console.error('[Quantity Report] Error details:', err.response?.data);
      setError('Failed to load quantity report. Please try again.');
      setQtyData([]);
    } finally {
      setLoading(false);
    }
  }, [qtyFilters, qtySort]);

  // Auto-fetch on tab change and filter changes
  useEffect(() => {
    if (activeTab === 'detail') fetchDetail();
  }, [activeTab, fetchDetail]);

  useEffect(() => {
    if (activeTab === 'quantity') fetchQuantity();
  }, [activeTab, fetchQuantity]);

  // Sort handler for quantity
  const handleQtySort = (field) => {
    setQtySort(prev => ({
      field,
      order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  const getQtySortIcon = (field) => {
    if (qtySort.field !== field) return <FaSort className="sort-icon" />;
    return qtySort.order === 'ASC'
      ? <FaSortUp className="sort-icon active" />
      : <FaSortDown className="sort-icon active" />;
  };

  // CSV Export
  const exportCSV = (headers, rows, filename) => {
    const escape = (val) => {
      if (val == null) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };
    const csv = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportDetailCSV = () => {
    const headers = ['Date', 'Product', 'Category', 'Type', 'Quantity', 'Unit Price', 'Total Amount', 'User', 'Notes'];
    const txList = detailData.transactions || Object.values(detailData.grouped || {}).flat();
    const rows = txList.map(t => [
      new Date(t.date).toLocaleDateString(), t.product, t.category, t.type,
      t.quantity, t.unitPrice.toFixed(2), t.totalAmount.toFixed(2), t.user, t.notes || ''
    ]);
    exportCSV(headers, rows, 'stock_detail_report.csv');
  };

  const resetDetailFilters = () => {
    setDetailFilters({ productId: '', categoryId: '', type: 'all', startDate: '', endDate: '', groupBy: '' });
  };

  const resetQtyFilters = () => {
    setQtyFilters({ categoryId: '', search: '' });
  };

  const hasActiveDetailFilters = detailFilters.productId || detailFilters.categoryId ||
    detailFilters.type !== 'all' || detailFilters.startDate || detailFilters.endDate || detailFilters.groupBy;

  const hasActiveQtyFilters = qtyFilters.categoryId || qtyFilters.search;

  const handlePrint = () => {
    window.print();
  };

  const exportQtyCSV = () => {
    const headers = ['Product Name', 'Category', 'Quantity'];
    const rows = qtyData.map(p => [p.name, p.category, p.stock]);
    exportCSV(headers, rows, 'product_quantities.csv');
  };

  return (
    <div className="stock-report-container">
      {/* Page Header */}
      <div className="report-page-header">
        <div className="header-left">
          <FaChartBar className="header-icon" />
          <div>
            <h1>Stock Reports</h1>
            <p className="header-subtitle">Analyze inventory levels and track stock movements</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="report-tabs">
        <button
          className={`report-tab ${activeTab === 'quantity' ? 'active' : ''}`}
          onClick={() => setActiveTab('quantity')}
        >
          <FaListUl className="tab-icon" />
          <span>Quantity Report</span>
          <small>Simple product quantities</small>
        </button>
        <button
          className={`report-tab ${activeTab === 'detail' ? 'active' : ''}`}
          onClick={() => setActiveTab('detail')}
        >
          <FaSearchPlus className="tab-icon" />
          <span>Detail Report</span>
          <small>Transaction history</small>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="report-error">
          <FaExclamationTriangle />
          <span>{error}</span>
          <button onClick={() => setError(null)}><FaTimes /></button>
        </div>
      )}

      {/* ===================== QUANTITY TAB ===================== */}
      {activeTab === 'quantity' && (
        <div className="report-content">
          {/* Filters */}
          <div className="filter-panel">
            <div className="filter-panel-header">
              <FaFilter className="filter-icon" />
              <span>Filters</span>
              {hasActiveQtyFilters && (
                <button className="filter-reset" onClick={resetQtyFilters}>
                  <FaRedo /> Reset
                </button>
              )}
            </div>
            <div className="filter-controls">
              <div className="filter-group">
                <label>Category</label>
                <select
                  className="filter-select"
                  value={qtyFilters.categoryId}
                  onChange={e => setQtyFilters(f => ({ ...f, categoryId: e.target.value }))}
                >
                  <option value="">All Categories</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Search</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by name or barcode..."
                  value={qtyFilters.search}
                  onChange={e => setQtyFilters(f => ({ ...f, search: e.target.value }))}
                />
              </div>
              <div className="filter-group filter-action">
                <button className="btn-export" onClick={handlePrint} disabled={!qtyData.length}>
                  <FaPrint /> Print
                </button>
                <button className="btn-export" onClick={exportQtyCSV} disabled={!qtyData.length}>
                  <FaDownload /> Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Simple Table */}
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading quantity data...</p>
            </div>
          ) : (
            <div className="table-wrapper quantity-report-wrapper">
              <div className="table-header-bar">
                <span className="table-title">Product Quantities</span>
                <span className="table-count">{qtyData.length} products</span>
              </div>
              <div className="table-responsive">
                <table className="report-table quantity-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleQtySort('name')} className="sortable">
                        Product Name {getQtySortIcon('name')}
                      </th>
                      <th onClick={() => handleQtySort('category')} className="sortable">
                        Category {getQtySortIcon('category')}
                      </th>
                      <th onClick={() => handleQtySort('stock')} className="sortable text-right">
                        Quantity {getQtySortIcon('stock')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {qtyData.length === 0 ? (
                      <tr>
                        <td colSpan="3">
                          <div className="empty-state">
                            <FaBoxOpen className="empty-icon" />
                            <p>No products found matching your filters</p>
                            {hasActiveQtyFilters && (
                              <button className="btn-link" onClick={resetQtyFilters}>Clear filters</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      qtyData.map(p => (
                        <tr key={p.id}>
                          <td className="cell-product">
                            <span className="product-name">{p.name}</span>
                            {p.barcode && <span className="product-barcode">{p.barcode}</span>}
                          </td>
                          <td><span className="category-pill">{p.category}</span></td>
                          <td className="text-right font-bold quantity-cell">{p.stock}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===================== DETAIL TAB ===================== */}
      {activeTab === 'detail' && (
        <div className="report-content">
          {/* Filters */}
          <div className="filter-panel">
            <div className="filter-panel-header">
              <FaFilter className="filter-icon" />
              <span>Filters</span>
              {hasActiveDetailFilters && (
                <button className="filter-reset" onClick={resetDetailFilters}>
                  <FaRedo /> Reset
                </button>
              )}
            </div>
            <div className="filter-controls">
              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={detailFilters.startDate}
                  onChange={e => setDetailFilters(f => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={detailFilters.endDate}
                  onChange={e => setDetailFilters(f => ({ ...f, endDate: e.target.value }))}
                />
              </div>
              <div className="filter-group">
                <label>Category</label>
                <select
                  className="filter-select"
                  value={detailFilters.categoryId}
                  onChange={e => setDetailFilters(f => ({ ...f, categoryId: e.target.value, productId: '' }))}
                >
                  <option value="">All Categories</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Product</label>
                <select
                  className="filter-select"
                  value={detailFilters.productId}
                  onChange={e => setDetailFilters(f => ({ ...f, productId: e.target.value }))}
                >
                  <option value="">All Products</option>
                  {products
                    .filter(p => !detailFilters.categoryId || String(p.CategoryId) === String(detailFilters.categoryId))
                    .map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Type</label>
                <select
                  className="filter-select"
                  value={detailFilters.type}
                  onChange={e => setDetailFilters(f => ({ ...f, type: e.target.value }))}
                >
                  <option value="all">All Types</option>
                  <option value="in">Stock In</option>
                  <option value="out">Stock Out</option>
                </select>
              </div>
              <div className="filter-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={detailFilters.groupBy === 'product'}
                    onChange={e => setDetailFilters(f => ({ ...f, groupBy: e.target.checked ? 'product' : '' }))}
                  />
                  <span>Group by Product</span>
                </label>
              </div>
              <div className="filter-group filter-action">
                <button className="btn-export" onClick={exportDetailCSV} disabled={!(detailData.transactions?.length || detailData.grouped)}>
                  <FaDownload /> Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {detailData.summary && (
            <div className="metric-cards">
              <div className="metric-card">
                <div className="metric-icon green"><FaArrowUp /></div>
                <div className="metric-body">
                  <span className="metric-value">{detailData.summary.totalIn.toLocaleString()}</span>
                  <span className="metric-label">Total Stock In</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon red"><FaArrowDown /></div>
                <div className="metric-body">
                  <span className="metric-value">{detailData.summary.totalOut.toLocaleString()}</span>
                  <span className="metric-label">Total Stock Out</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon blue"><FaExchangeAlt /></div>
                <div className="metric-body">
                  <span className={`metric-value ${detailData.summary.netChange >= 0 ? 'text-success' : 'text-danger'}`}>
                    {detailData.summary.netChange >= 0 ? '+' : ''}{detailData.summary.netChange.toLocaleString()}
                  </span>
                  <span className="metric-label">Net Change</span>
                </div>
              </div>
              <div className="metric-card">
                <div className="metric-icon purple"><FaMoneyBillWave /></div>
                <div className="metric-body">
                  <span className="metric-value">${detailData.summary.totalValue.toLocaleString()}</span>
                  <span className="metric-label">Total Value</span>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading transaction data...</p>
            </div>
          ) : detailFilters.groupBy === 'product' && detailData.grouped ? (
            // Grouped view
            <div className="grouped-tables">
              {Object.keys(detailData.grouped).length === 0 ? (
                <div className="table-wrapper">
                  <div className="empty-state">
                    <FaSearchPlus className="empty-icon" />
                    <p>No transactions found matching your filters</p>
                    {hasActiveDetailFilters && (
                      <button className="btn-link" onClick={resetDetailFilters}>Clear filters</button>
                    )}
                  </div>
                </div>
              ) : (
                Object.entries(detailData.grouped).map(([productName, txs]) => {
                  const groupIn = txs.filter(t => t.type === 'in').reduce((s, t) => s + t.quantity, 0);
                  const groupOut = txs.filter(t => t.type === 'out').reduce((s, t) => s + t.quantity, 0);
                  return (
                    <div key={productName} className="table-wrapper grouped-section">
                      <div className="table-header-bar grouped-header">
                        <div className="group-title-row">
                          <span className="table-title">{productName}</span>
                          <div className="group-badges">
                            <span className="group-stat in">+{groupIn} in</span>
                            <span className="group-stat out">-{groupOut} out</span>
                            <span className="table-count">{txs.length} transactions</span>
                          </div>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="report-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Type</th>
                              <th className="text-right">Quantity</th>
                              <th className="text-right">Unit Price</th>
                              <th className="text-right">Total</th>
                              <th>User</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {txs.map(t => (
                              <tr key={t.id}>
                                <td>{new Date(t.date).toLocaleDateString()}</td>
                                <td>
                                  <span className={`type-badge type-${t.type}`}>
                                    {t.type === 'in' ? <><FaArrowUp /> In</> : <><FaArrowDown /> Out</>}
                                  </span>
                                </td>
                                <td className="text-right font-bold">{t.quantity}</td>
                                <td className="text-right">${t.unitPrice.toFixed(2)}</td>
                                <td className="text-right font-bold">${t.totalAmount.toFixed(2)}</td>
                                <td>{t.user}</td>
                                <td className="text-muted">{t.notes || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            // Flat view
            <div className="table-wrapper">
              <div className="table-header-bar">
                <span className="table-title">Transaction History</span>
                <span className="table-count">
                  {detailData.transactions?.length || 0} transactions
                </span>
              </div>
              <div className="table-responsive">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th className="text-right">Quantity</th>
                      <th className="text-right">Unit Price</th>
                      <th className="text-right">Total</th>
                      <th>User</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!detailData.transactions || detailData.transactions.length === 0) ? (
                      <tr>
                        <td colSpan="9">
                          <div className="empty-state">
                            <FaSearchPlus className="empty-icon" />
                            <p>No transactions found matching your filters</p>
                            {hasActiveDetailFilters && (
                              <button className="btn-link" onClick={resetDetailFilters}>Clear filters</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      detailData.transactions.map(t => (
                        <tr key={t.id}>
                          <td className="text-nowrap">{new Date(t.date).toLocaleDateString()}</td>
                          <td className="font-medium">{t.product}</td>
                          <td><span className="category-pill">{t.category}</span></td>
                          <td>
                            <span className={`type-badge type-${t.type}`}>
                              {t.type === 'in' ? <><FaArrowUp /> In</> : <><FaArrowDown /> Out</>}
                            </span>
                          </td>
                          <td className="text-right font-bold">{t.quantity}</td>
                          <td className="text-right">${t.unitPrice.toFixed(2)}</td>
                          <td className="text-right font-bold">${t.totalAmount.toFixed(2)}</td>
                          <td>{t.user}</td>
                          <td className="text-muted">{t.notes || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockReport;
